import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectLanesTiers } from 'app/pages/Home/slice/selectors';
import { MomentData, MilestonesGridData } from '../types';

/**
 * Custom hook to filter milestones grid data based on selected filters from Redux state
 * Phase 1: Filters by Lanes/Tiers
 * Future phases: Will add more filter types
 */
export const useFilterMilestones = (
  milestonesData: MilestonesGridData | null
): MilestonesGridData | null => {
  // Get selected tiers from Redux state
  const lanesTiers = useSelector(selectLanesTiers);
  const selectedTiers = lanesTiers?.selectedLanesTiers || [];

  // Memoize the filtered data to avoid unnecessary recalculations
  const filteredData = useMemo(() => {
    // If no data, return null
    if (!milestonesData || !milestonesData.data) {
      return null;
    }

    // If no filters are selected, return original data
    if (selectedTiers.length === 0) {
      return milestonesData;
    }

    // Map filter values to match data values
    // 'all-other-releases' in filter -> 'Other' in data
    // 'tbd' in filter -> 'TBD' in data
    const mapFilterToDataValue = (filterValue: string): string => {
      const lowerValue = filterValue.toLowerCase();
      if (lowerValue === 'all-other-releases') {
        return 'other';
      }
      if (lowerValue === 'tbd') {
        return 'tbd';
      }
      return lowerValue;
    };

    // Filter the data based on selected tiers
    const filtered: MomentData[] = milestonesData.data.filter(moment => {
      // Check if the moment's tier matches any of the selected tiers
      const momentTier = moment.metaDetails?.tier;

      // If tier doesn't exist, exclude it
      if (!momentTier) {
        return false;
      }

      // Case-insensitive comparison: convert both to lowercase and map filter values
      const momentTierLower = momentTier.toLowerCase();
      const mappedSelectedTiers = selectedTiers.map(tier =>
        mapFilterToDataValue(tier)
      );

      // If tier exists and matches one of the selected tiers, include it
      return mappedSelectedTiers.includes(momentTierLower);
    });

    return {
      ...milestonesData,
      data: filtered
    };
  }, [milestonesData, selectedTiers]);

  return filteredData;
};
